/**
 * Configures the menu layout.
 */
import type { RouteConfig } from "vue-router";
import type {
  Location,
  Route,
  RoutePropsFunction,
} from "vue-router/types/router";
import type { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import Guid from "@/app/types/common/guid";
import type {
  IRouteItem,
  IRouteWithLocation,
  RouteMetadata,
} from "@/app/ui/navigation/types";

export type BuildRouteOpts = {
  url: string;
  view: () => Promise<any>;
  componentProps?: boolean | Record<string, any> | RoutePropsFunction;
  preSession?: boolean;
  requiredUserPermissions?: EnumUserPermission[];
  routeKey?: (route: Route) => string;
  childRoutes?: RouteConfig[];
};

export type SubRouteOpts = {
  path: string;
  component: () => Promise<any>;
};

export type ISubRouteWithLocation<TParams extends any[]> = RouteConfig & {
  location: (...params: TParams) => Location;
};

export class RouteBuilder {
  private _nameBase: string;
  private _routes: IRouteItem[] = [];
  private _nextId = 1;

  constructor(nameBase: string) {
    this._nameBase = nameBase;
  }

  public getRoutes(): readonly IRouteItem[] {
    return this._routes.slice();
  }

  private buildAndAddRoute<TParamsFunc extends (...params: any) => any>(
    opts: BuildRouteOpts,
    makeLocationParams: (...p: Parameters<TParamsFunc>) => any,
    makeQuery?: (...p: Parameters<TParamsFunc>) => any
  ) {
    const meta: RouteMetadata = {
      routeKey: opts.routeKey,
    };

    if (opts.preSession !== undefined) {
      meta.preSession = opts.preSession;
    }

    if (opts.routeKey) {
      for (const child of opts.childRoutes ?? []) {
        child.meta ??= {};
        child.meta.routeKey ??= opts.routeKey;
      }
    }

    const route = {
      name: this._nameBase + "_" + this._nextId++,
      url: opts.url,
      view: opts.view,
      componentProps: opts.componentProps,
      routeMetadata: meta,
      requiredUserPermissions: opts.requiredUserPermissions,
      childRoutes: opts.childRoutes ?? [],
      location: (...ps: Parameters<TParamsFunc>): Location => ({
        name: route.name,
        params: makeLocationParams(...ps),
        query: makeQuery?.(...ps),
      }),
    };

    this._routes.push(route);
    return route;
  }

  route(opts: BuildRouteOpts): IRouteWithLocation<[]> {
    return this.buildAndAddRoute(opts, () => ({}));
  }

  routeWithParams<TParamsFunc extends (params: any) => any>(
    opts: BuildRouteOpts,
    locationParams: TParamsFunc
  ): IRouteWithLocation<Parameters<TParamsFunc>> {
    return this.buildAndAddRoute(opts, locationParams);
  }

  routeWithQuery<TParams>(
    opts: BuildRouteOpts,
    buildQuery: (opts: TParams) => any
  ): IRouteWithLocation<[TParams]> {
    return this.buildAndAddRoute(opts, () => {}, buildQuery);
  }

  routeWithSingleIdParam(opts: BuildRouteOpts): IRouteWithLocation<[id: Guid]> {
    return this.routeWithParams(
      Object.assign(
        {
          componentProps: (route: Route) => ({
            id: Guid.parseOrThrow(route.params.id),
          }),
        },
        opts
      ),
      (id: Guid) => ({
        id: id.toString(),
      })
    );
  }

  routeToHoldSubRoutes(opts: BuildRouteOpts): IRouteItem {
    return this.route(opts);
  }

  subRoute<TParamsFunc extends (params: any) => any>(
    opts: SubRouteOpts,
    locationParams: TParamsFunc
  ): ISubRouteWithLocation<Parameters<TParamsFunc>> {
    const route = {
      name: this._nameBase + "_sub_" + this._nextId++,
      path: opts.path,
      component: opts.component,
      location: (...ps: Parameters<TParamsFunc>): Location => ({
        name: route.name,
        params: locationParams(ps),
      }),
    };

    return route;
  }

  subRouteWithSingleIdParam(
    opts: SubRouteOpts
  ): ISubRouteWithLocation<[id: Guid]> {
    return this.subRoute(
      Object.assign(
        {
          //componentProps: (route: Route) => ({
          //id: Guid.parseOrThrow(route.params.id),
          //}),
        },
        opts
      ),
      (id: Guid) => ({
        id: id.toString(),
      })
    );
  }
}
